/**
 * MaterialTrackingPanel — live inventory pivoted by warehouse location.
 * Rendered inside Project Controls as a sub-module. Only shows
 * IQC-Accepted stock (real usable inventory) rather than every MCI.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Warehouse, Package, RefreshCw, Loader2, FileText, ClipboardCheck, Wand2, X,
         ChevronRight, ChevronDown, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import UniversalFilterBar from '@/components/procurement/UniversalFilterBar';
import MciPackageDrawer from '@/components/procurement/MciPackageDrawer';
import PoPerformanceDrawer from '@/components/procurement/PoPerformanceDrawer';

const C = { primary: '#1E4E8C', primaryText: 'var(--tone-navy-text)', accent: '#F26B1F', slate: 'var(--text-primary)',
             slateMuted: 'var(--text-muted)', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444' };

function fmtNum(n) { return Number(n || 0).toLocaleString(); }

function BackfillDialog({ projectId, onClose, onApplied }) {
  const [strategy, setStrategy] = useState('single');
  const [location, setLocation] = useState('WH-01 · Main Warehouse');
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (strategy === 'single' && !location.trim()) {
      toast.error('Bin name is required'); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/material-tracking/backfill-locations`,
        { strategy, location: strategy === 'single' ? location.trim() : null, updateAccepted: true });
      toast.success(`Tagged ${data.updatedReceipts} receipts across ${data.updatedMcis} MCIs → ${data.binsTouched.length} bins`);
      onApplied();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         onClick={onClose} data-testid="backfill-dialog">
      <div className="bg-[var(--card-bg)] rounded-xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-2">
            <Wand2 className="w-4 h-4" style={{ color: C.accent }} />
            <div>
              <div className="text-sm font-bold" style={{ color: C.slate }}>Backfill Warehouse Locations</div>
              <div className="text-[10px]" style={{ color: C.slateMuted }}>Assigns every UNASSIGNED receipt in one shot</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="backfill-close"><X className="w-4 h-4" style={{ color: C.slateMuted }} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase font-semibold mb-1.5" style={{ color: C.slateMuted }}>Strategy</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: 'single', l: 'Single Bin', h: 'All UNASSIGNED → one bin' },
                { v: 'by_discipline', l: 'Auto by Discipline', h: 'PIP → Piping, ELE → E&I, …' },
              ].map(o => (
                <button key={o.v} onClick={() => setStrategy(o.v)}
                  className="p-2 rounded-lg text-left border transition-colors"
                  data-testid={`backfill-strategy-${o.v}`}
                  style={{
                    borderColor: strategy === o.v ? C.primaryText : C.border,
                    background: strategy === o.v ? 'var(--tone-blue-bg)' : '#fff',
                  }}>
                  <div className="text-xs font-bold" style={{ color: C.slate }}>{o.l}</div>
                  <div className="text-[9px]" style={{ color: C.slateMuted }}>{o.h}</div>
                </button>
              ))}
            </div>
          </div>
          {strategy === 'single' && (
            <div>
              <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Target Bin Name</div>
              <input value={location} onChange={e => setLocation(e.target.value)}
                className="w-full px-2 py-1.5 rounded-md text-sm border"
                style={{ borderColor: C.border, color: C.slate }}
                placeholder="e.g. WH-01 · Main Warehouse"
                data-testid="backfill-location-input" />
            </div>
          )}
          {strategy === 'by_discipline' && (
            <div className="text-[10px] p-2 rounded" style={{ background: 'var(--tone-amber-bg)', color: 'var(--tone-amber-text)' }}>
              Default mapping: Piping→WH-01, Structural→WH-02, Mechanical→WH-03, E&I→WH-04, Civil→WH-05, HVAC→WH-06, Fire&Safety→WH-07. Unknown disciplines fall back to WH-99 · Unallocated.
            </div>
          )}
        </div>
        <div className="p-3 border-t flex justify-end gap-2" style={{ borderColor: C.border }}>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={apply} disabled={busy}
            className="text-white font-semibold"
            style={{ background: C.accent }} data-testid="backfill-apply-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
            Apply Backfill
          </Button>
        </div>
      </div>
    </div>
  );
}

function BinCard({ bin, catFilter, disciplineFilter, q, onDrillMci, onPoPerf, projectId }) {
  const [open, setOpen] = useState(true);
  const [expandedCode, setExpandedCode] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [reservations, setReservations] = useState({});   // materialCode → [{tag, qty, marks:[]}]
  // NEW · sort mode for the per-tag allocation drilldown
  const [drilldownSort, setDrilldownSort] = useState('priority');   // 'priority' | 'date' | 'qty'

  // Fetch the per-material-code ledger for this warehouse once; every
  // expanded row reads from this cache so numbers align exactly with the
  // Site Map drilldown.
  useEffect(() => {
    if (!open || ledger || !projectId) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/mtx/warehouses/${encodeURIComponent(bin.location)}/material-analysis`);
        setLedger(data);
      } catch { setLedger({ materialCodes: [], totals: null }); }
    })();
  }, [open, ledger, projectId, bin.location]);

  // Fetch reservations for this bin (which units are ring-fenced for
  // which tag/mark).  Feeds the "Allocated for tag XXXX" chip.
  //
  // NOTE: `mtx_material_units.materialCode` (long, e.g. "HR Bolt M16x45
  // Gr 8.8 + nut + ") does NOT equal `bin.items[].materialCode` which
  // often falls back to `mciEntries.bulkItemCode` (short, e.g. "HR
  // Bolt"). The backend now returns a `matchKeys[]` alias list per
  // reservation so we can map either way.
  useEffect(() => {
    if (!open || !projectId) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/swm/allocation/reservations?bin_=${encodeURIComponent(bin.location)}`);
        const idx = {};
        (data.reservations || []).forEach(r => {
          const keys = (r.matchKeys && r.matchKeys.length) ? r.matchKeys : [r.materialCode];
          keys.forEach(k => { if (k && !idx[k]) idx[k] = r.byTag || []; });
        });
        setReservations(idx);
      } catch { setReservations({}); }
    })();
  }, [open, projectId, bin.location]);
  const ledgerByCode = {};
  (ledger?.materialCodes || []).forEach(r => {
    const acc = ledgerByCode[r.materialCode] || {
      poQty: 0, iqcAccepted: 0, issued: 0, returned: 0,
      reconciledVariance: 0, finalInventory: 0, unit: r.unit };
    acc.poQty += r.poQty; acc.iqcAccepted += r.iqcAccepted;
    acc.issued += r.issued; acc.returned += r.returned;
    acc.reconciledVariance += r.reconciledVariance;
    acc.finalInventory += r.finalInventory;
    ledgerByCode[r.materialCode] = acc;
  });

  // 1. Filter raw receipts by search / category / discipline
  const filtered = (bin.items || []).filter(i => {
    if (catFilter !== 'ALL' && (i.materialCategory || '').toUpperCase() !== catFilter) return false;
    if (disciplineFilter && (i.discipline || '') !== disciplineFilter) return false;
    if (q && !`${i.mciLineNumber} ${i.materialCode || ''} ${i.materialDescription || ''} ${i.vendor || ''} ${i.receiptNumber || ''}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  // Site-station racks (STATION_RACK / SS-*) must ALWAYS render — even
  // when empty — so planners see the full 8-rack station picture.
  // Warehouse bins can still short-circuit when there's nothing to
  // show, which keeps the panel dense.
  const isStationRack = bin.warehouseType === 'STATION_RACK'
                        || String(bin.location || '').startsWith('SS-');
  if (filtered.length === 0 && !isStationRack) return null;

  // 2. Aggregate by materialCode → one row per code with underlying receipts collapsed
  const groups = {};
  for (const it of filtered) {
    const code = it.materialCode || '—';
    if (!groups[code]) {
      groups[code] = {
        materialCode: code,
        materialDescription: it.materialDescription,
        vendors: new Set(), mcis: new Set(), receipts: [],
        totalAccepted: 0, unit: it.unit,
      };
    }
    const g = groups[code];
    if (it.vendor) g.vendors.add(it.vendor);
    if (it.mciLineNumber) g.mcis.add(it.mciLineNumber);
    g.receipts.push(it);
    g.totalAccepted += Number(it.acceptedQty || 0);
  }
  const groupRows = Object.values(groups)
    .map(g => ({ ...g, vendors: [...g.vendors], mcis: [...g.mcis] }))
    .sort((a, b) => b.totalAccepted - a.totalAccepted);

  return (
    <div className="mb-3 rounded-lg border overflow-hidden" style={{ borderColor: C.border }}
      data-testid={`mt-bin-${bin.location}`}>
      <div className="p-2 flex items-center gap-2 cursor-pointer"
        onClick={() => setOpen(!open)}
        style={{ background: bin.location === 'UNASSIGNED' ? 'var(--tone-red-bg)' : 'var(--tone-blue-bg)' }}>
        <Warehouse className="w-4 h-4" style={{ color: bin.location === 'UNASSIGNED' ? C.danger : C.primaryText }} />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-bold" style={{ color: C.primaryText }}>{bin.location}</span>
            <span className="text-[9.5px]" style={{ color: C.slateMuted }}>{bin.discipline}</span>
            {(bin.gpsLat != null && bin.gpsLng != null) && (
              <a href={`https://www.google.com/maps?q=${bin.gpsLat},${bin.gpsLng}`} target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Open in Google Maps · click to copy coords"
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-mono hover:brightness-95"
                style={{ background: 'var(--tone-blue-bg)', color: 'var(--tone-navy-text)' }}
                data-testid={`mt-bin-geo-${bin.location}`}>
                📍 {Number(bin.gpsLat).toFixed(3)}°{bin.gpsLat >= 0 ? 'N' : 'S'}, {Number(bin.gpsLng).toFixed(3)}°{bin.gpsLng >= 0 ? 'E' : 'W'}
              </a>
            )}
          </div>
          <div className="text-[9.5px]" style={{ color: C.slateMuted }}>
            {(bin.items || []).length > 0 ? (
              <>{new Set((bin.items || []).map(i => i.materialCode).filter(Boolean)).size} distinct material codes · {(bin.items || []).length} receipts · {new Set((bin.items || []).map(i => i.mciLineNumber).filter(Boolean)).size} MCIs</>
            ) : 'Empty rack — awaiting inbound MIR'}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase" style={{ color: C.slateMuted }}>Balance / Issued</div>
          <div className="text-lg font-bold font-mono" style={{ color: C.success }}>
            {fmtNum(bin.totalBalance ?? bin.totalAccepted)}
            <span className="text-[10px] font-normal ml-1" style={{ color: C.accent }}>
              ({fmtNum(bin.totalIssued || 0)} issued)
            </span>
          </div>
        </div>
      </div>
      {/* Capacity fill bar — balance ÷ rack capacity.
          When the rack is fully depleted (balance = 0 but qty was
          issued) we flip the row into a "Fully depleted — N units
          issued" chip instead of showing the misleading 0.0% gauge. */}
      {typeof bin.capacity === 'number' && bin.capacity > 0 && (() => {
        const depleted = (bin.totalBalance || 0) === 0 && (bin.totalIssued || 0) > 0;
        if (depleted) {
          return (
            <div className="px-2 py-1.5 border-t flex items-center gap-2"
              style={{ borderColor: C.border, background: 'var(--tone-amber-bg)' }}
              data-testid={`mt-bin-depleted-${bin.location}`}>
              <div className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded"
                style={{ background: '#F59E0B', color: '#fff' }}>
                Fully Depleted
              </div>
              <div className="text-[10px] font-semibold" style={{ color: 'var(--tone-amber-text)' }}>
                {fmtNum(bin.totalIssued)} units issued · capacity {fmtNum(bin.capacity)} available for re-stock
              </div>
            </div>
          );
        }
        return (
          <div className="px-2 py-1.5 border-t flex items-center gap-2" style={{ borderColor: C.border, background: 'var(--tone-slate-bg)' }}
            data-testid={`mt-bin-capacity-${bin.location}`}>
            <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>Rack Capacity</div>
            <div className="flex-1 relative rounded-full overflow-hidden" style={{ height: 8, background: 'var(--border-color)' }}>
              <div className="absolute inset-y-0 left-0 rounded-full transition-all"
                style={{
                  width: `${bin.fillPct || 0}%`,
                  background: (bin.fillPct || 0) >= 90 ? '#EF4444'
                           : (bin.fillPct || 0) >= 70 ? '#F59E0B'
                           : '#10B981',
                }} />
            </div>
            <div className="text-[10px] font-mono font-bold" style={{ color: C.slate, minWidth: 90 }}>
              {(bin.fillPct || 0).toFixed(1)}%
              <span className="text-[9px] font-normal ml-1" style={{ color: C.slateMuted }}>
                {fmtNum(bin.totalBalance)} / {fmtNum(bin.capacity)}
              </span>
            </div>
          </div>
        );
      })()}
      {open && (
        <table className="w-full text-[11px]">
          <thead style={{ background: 'var(--card-bg-alt)' }}>
            <tr>
              <th className="w-6 p-1.5"></th>
              <th className="text-left p-1.5 uppercase text-[9px]" style={{ color: C.slateMuted }}>Material Code</th>
              <th className="text-left p-1.5 uppercase text-[9px]" style={{ color: C.slateMuted }}>Description</th>
              <th className="text-left p-1.5 uppercase text-[9px]" style={{ color: C.slateMuted }}>Vendor</th>
              <th className="text-right p-1.5 uppercase text-[9px]" style={{ color: C.slateMuted }}>MCIs · Receipts</th>
              <th className="text-right p-1.5 uppercase text-[9px]" style={{ color: C.slateMuted }}>Total on Hand</th>
              <th className="text-right p-1.5 uppercase text-[9px]" style={{ color: 'var(--tone-purple-text)' }}>Allocated</th>
              <th className="text-right p-1.5 uppercase text-[9px]" style={{ color: C.success }}>Free</th>
              <th className="text-center p-1.5 uppercase text-[9px]" style={{ color: C.slateMuted }}>Docs</th>
            </tr>
          </thead>
          <tbody>
            {groupRows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-4 text-center text-[11px]"
                  style={{ color: C.slateMuted, background: 'var(--tone-slate-bg)' }}
                  data-testid={`mt-bin-empty-${bin.location}`}>
                  No stock on this rack yet — inbound MIR issues will land here.
                </td>
              </tr>
            )}
            {groupRows.map((g, i) => {
              const isExpanded = expandedCode === g.materialCode;
              const anyDocs = g.receipts.some(r => r.documentRef || r.inspectionReportRef);
              const tagRes = (() => {
                // Try each candidate key in decreasing specificity so we
                // catch matches whether the bin row keys off the full
                // materialCode, bulkItemCode short-form, or first-word.
                const candidates = [
                  g.materialCode,
                  (g.materialDescription || '').slice(0, 30).trim(),
                  (g.materialDescription || '').slice(0, 20).trim(),
                  (g.materialCode || '').split(' ').slice(0, 3).join(' '),
                  (g.materialCode || '').split(' ').slice(0, 2).join(' '),
                  (g.materialDescription || '').split(' ').slice(0, 3).join(' '),
                  (g.materialDescription || '').split(' ').slice(0, 2).join(' '),
                ];
                for (const k of candidates) {
                  if (k && reservations[k] && reservations[k].length) return reservations[k];
                }
                return [];
              })();
              const allocatedQty = tagRes.reduce((s, t) => s + Number(t.qty || 0), 0);
              const freeQty = Math.max(g.totalAccepted - allocatedQty, 0);
              return (
                <React.Fragment key={g.materialCode + i}>
                  <tr style={{ borderTop: `1px solid ${C.border}`, cursor: 'pointer' }}
                    onClick={() => setExpandedCode(isExpanded ? null : g.materialCode)}
                    className="hover:bg-black/[0.02]"
                    data-testid={`mt-group-${g.materialCode}`}>
                    <td className="p-1.5">
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5" style={{ color: C.slateMuted }} />
                                  : <ChevronRight className="w-3.5 h-3.5" style={{ color: C.slateMuted }} />}
                    </td>
                    <td className="p-1.5 font-mono text-[10px] font-bold whitespace-nowrap"
                      style={{ color: C.primaryText }}
                      title={g.materialCode}>
                      {g.materialCode}
                    </td>
                    <td className="p-1.5" style={{ color: C.slate }}>
                      <div className="text-[11px] max-w-[280px] truncate" title={g.materialDescription}>{g.materialDescription}</div>
                    </td>
                    <td className="p-1.5 text-[10px]" style={{ color: C.slate }}>
                      {g.vendors.join(', ') || '—'}
                    </td>
                    <td className="p-1.5 text-right font-mono text-[10px]" style={{ color: C.slateMuted }}>
                      {g.mcis.length} · {g.receipts.length}
                    </td>
                    <td className="p-1.5 text-right font-mono font-bold" style={{ color: C.slate }}>
                      {fmtNum(g.totalAccepted)} <span className="text-[9px] font-normal" style={{ color: C.slateMuted }}>{g.unit}</span>
                    </td>
                    <td className="p-1.5 text-right font-mono font-bold" style={{ color: allocatedQty > 0 ? '#7C3AED' : C.slateMuted }} data-testid={`mt-allocated-${g.materialCode}`}>
                      {allocatedQty > 0 ? fmtNum(allocatedQty) : '—'}
                      {allocatedQty > 0 && (
                        <div className="text-[8.5px] font-normal mt-0.5" style={{ color: 'var(--tone-purple-text)' }}>
                          {tagRes.length === 1 ? tagRes[0].tag : `${tagRes.length} tags · click ▸ for breakdown`}
                        </div>
                      )}
                    </td>
                    <td className="p-1.5 text-right font-mono font-bold" style={{ color: freeQty > 0 ? C.success : C.danger }}>
                      {fmtNum(freeQty)}
                    </td>
                    <td className="p-1.5 text-center">
                      {anyDocs && <FileText className="w-3 h-3 inline" style={{ color: C.primaryText }} />}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={9} className="p-0" style={{ background: 'var(--tone-slate-bg)' }}>
                        <div className="p-2">
                          {tagRes.length > 0 && (
                            <div className="mb-2 rounded border-l-2 p-2" style={{ borderColor: '#A855F7', background: 'var(--tone-purple-bg)' }}>
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="text-[10px] font-semibold uppercase flex items-center gap-1.5" style={{ color: 'var(--tone-purple-text)' }}>
                                  <span>🔒 Allocation breakdown ({fmtNum(allocatedQty)} {g.unit} committed · {freeQty > 0 ? fmtNum(freeQty) + ' ' + g.unit + ' still free' : 'nothing free'})</span>
                                </div>
                                <div className="flex items-center gap-0.5 text-[9px]" data-testid={`mt-drilldown-sort-${g.materialCode}`}>
                                  <span style={{ color: C.slateMuted }} className="mr-1">Sort:</span>
                                  {[
                                    { k: 'priority', l: 'Priority' },
                                    { k: 'date', l: 'Date' },
                                    { k: 'qty', l: 'Qty' },
                                  ].map(o => (
                                    <button key={o.k} onClick={(e) => { e.stopPropagation(); setDrilldownSort(o.k); }}
                                      className="px-1.5 py-0.5 rounded font-semibold"
                                      style={{ background: drilldownSort === o.k ? '#7C3AED' : 'var(--tone-purple-bg)',
                                                color: drilldownSort === o.k ? '#fff' : '#7C3AED' }}
                                      data-testid={`mt-drilldown-sort-${o.k}`}>
                                      {o.l}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <table className="w-full text-[10px]" data-testid={`mt-alloc-drilldown-${g.materialCode}`}>
                                <thead>
                                  <tr style={{ background: 'var(--tone-purple-bg)' }}>
                                    <th className="p-1 text-left uppercase text-[8.5px]" style={{ color: 'var(--tone-purple-text)' }}>Tag</th>
                                    <th className="p-1 text-left uppercase text-[8.5px]" style={{ color: 'var(--tone-purple-text)' }}>Priority</th>
                                    <th className="p-1 text-left uppercase text-[8.5px]" style={{ color: 'var(--tone-purple-text)' }}>Macro Activity</th>
                                    <th className="p-1 text-left uppercase text-[8.5px]" style={{ color: 'var(--tone-purple-text)' }}>Mark #</th>
                                    <th className="p-1 text-right uppercase text-[8.5px]" style={{ color: 'var(--tone-purple-text)' }}>Qty</th>
                                    <th className="p-1 text-left uppercase text-[8.5px]" style={{ color: 'var(--tone-purple-text)' }}>Drawing / WP</th>
                                    <th className="p-1 text-left uppercase text-[8.5px]" style={{ color: 'var(--tone-purple-text)' }}>Lifecycle · Soft → Hard → Issued → Installed</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const sorted = [...tagRes].sort((a, b) => {
                                      if (drilldownSort === 'qty') return Number(b.qty || 0) - Number(a.qty || 0);
                                      if (drilldownSort === 'date') {
                                        const ad = a.reservedAt || '9999';
                                        const bd = b.reservedAt || '9999';
                                        return String(ad).localeCompare(String(bd));
                                      }
                                      // 'priority' (default) — server already sorted P1→Pn
                                      const ap = a.priority == null ? 999 : a.priority;
                                      const bp = b.priority == null ? 999 : b.priority;
                                      return ap - bp;
                                    });
                                    return sorted.map((t) => (
                                    (t.marks || []).map((m, mi) => {
                                      const lc = m.lifecycle || {};
                                      const stages = [
                                        { key: 'soft',      label: 'Soft',      color: '#3B82F6', date: lc.softDate },
                                        { key: 'hard',      label: 'Hard',      color: 'var(--tone-purple-text)', date: lc.hardDate },
                                        { key: 'issued',    label: 'Issued',    color: '#F59E0B', date: lc.issuedDate },
                                        { key: 'installed', label: 'Installed', color: '#10B981', date: lc.installedDate },
                                      ];
                                      return (
                                      <tr key={t.tag + '|' + m.markNumber + '|' + mi} style={{ borderTop: '1px solid #F3E8FF' }}>
                                        <td className="p-1 font-mono font-semibold" style={{ color: 'var(--tone-purple-text)' }}>{t.tag}</td>
                                        <td className="p-1">
                                          {t.priority
                                            ? <span className="px-1.5 py-0.5 rounded text-[8.5px] font-bold" style={{ background: t.priority === 1 ? '#EF444420' : t.priority === 2 ? '#F5920620' : t.priority === 3 ? '#EAB30820' : '#A855F720', color: t.priority === 1 ? '#EF4444' : t.priority === 2 ? '#F59206' : t.priority === 3 ? '#EAB308' : '#7C3AED' }}>P{t.priority}·{t.subPriority ?? '—'}</span>
                                            : <span style={{ color: C.slateMuted }}>—</span>}
                                        </td>
                                        <td className="p-1 text-[10px]" style={{ color: C.slate }}>
                                          {t.linkedActivityWBS ? <span className="font-mono text-[8.5px] mr-1" style={{ color: 'var(--tone-navy-text)' }}>{t.linkedActivityWBS}</span> : null}
                                          {t.linkedActivity || <span style={{ color: C.slateMuted }} className="italic">Not linked</span>}
                                        </td>
                                        <td className="p-1 font-mono" style={{ color: C.slate }}>{m.markNumber || '—'}</td>
                                        <td className="p-1 text-right font-mono font-semibold" style={{ color: 'var(--tone-purple-text)' }}>{fmtNum(m.qty)} <span className="text-[8.5px] font-normal" style={{ color: C.slateMuted }}>{g.unit}</span></td>
                                        <td className="p-1 text-[9.5px]" style={{ color: C.slate }}>
                                          {m.drawingRef || m.workPackage || <span style={{ color: C.slateMuted }}>—</span>}
                                        </td>
                                        <td className="p-1">
                                          <div className="flex items-center gap-0" data-testid={`mt-lifecycle-${m.markNumber}`}>
                                            {stages.map((s, si) => (
                                              <div key={s.key} className="flex items-center">
                                                <div className="flex flex-col items-center min-w-[52px]" title={`${s.label}${s.date ? ' · ' + String(s.date).slice(0, 10) : ' · pending'}`}>
                                                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: s.date ? s.color : 'var(--border-color)', boxShadow: s.date ? `0 0 0 2px ${s.color}30` : 'none' }} />
                                                  <div className="text-[8px] mt-0.5" style={{ color: s.date ? s.color : 'var(--text-muted)', fontWeight: s.date ? 700 : 500 }}>{s.label}</div>
                                                  <div className="text-[7.5px] font-mono" style={{ color: s.date ? 'var(--text-muted)' : '#CBD5E1' }}>{s.date ? String(s.date).slice(5, 10) : '—'}</div>
                                                </div>
                                                {si < stages.length - 1 && (
                                                  <div className="w-3 h-0.5 mx-0.5" style={{ background: stages[si + 1].date ? stages[si + 1].color : 'var(--border-color)', marginTop: -14 }} />
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </td>
                                      </tr>);
                                    })
                                  ));
                                  })()}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {/* Material-code ledger strip — PO · Available · Issued · Returned · Reconciled · Final */}
                          {(() => {
                            const L = ledgerByCode[g.materialCode];
                            if (!L) return null;
                            // fmt: show explicit 0 (not em-dash) so a real
                            // zero balance is distinguishable from "no data".
                            const fmt = (n) => (n === undefined || n === null)
                              ? '—'
                              : (Math.abs(n) >= 1000 ? Number(n).toLocaleString() : Number(n));
                            const cells = [
                              { l: 'PO Qty',            v: fmt(L.poQty),                c: C.slate },
                              { l: 'Available (IQC-OK)',v: fmt(L.iqcAccepted),          c: C.success },
                              { l: 'Field Issued',      v: fmt(L.issued),               c: C.accent },
                              { l: 'Field Returned',    v: fmt(L.returned),             c: C.primaryText },
                              { l: 'Reconciled Δ',      v: L.reconciledVariance ? (L.reconciledVariance > 0 ? '+' : '') + fmt(L.reconciledVariance) : fmt(0), c: L.reconciledVariance < 0 ? C.danger : C.slateMuted },
                              { l: 'Final Inventory',   v: fmt(L.finalInventory),       c: C.primaryText, bold: true },
                            ];
                            return (
                              <div className="grid grid-cols-6 gap-1.5 mb-2 p-1.5 rounded"
                                style={{ background: 'var(--card-bg-alt)', border: `1px solid ${C.border}` }}
                                data-testid={`mt-ledger-${g.materialCode}`}>
                                {cells.map((c, ci) => (
                                  <div key={ci} className="p-1 rounded bg-[var(--card-bg)] text-center"
                                    style={{ border: `1px solid ${C.border}` }}>
                                    <div className="text-[8.5px] uppercase font-semibold" style={{ color: C.slateMuted }}>{c.l}</div>
                                    <div className="text-[13px] font-mono" style={{ color: c.c, fontWeight: c.bold ? 800 : 600 }}>{c.v}</div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                          <div className="text-[9px] uppercase font-bold mb-1" style={{ color: C.slateMuted }}>
                            Underlying receipts · click any MCI to open full Pre-Order · Post-Order · MR · PO · GRN · IQC history
                          </div>
                          <table className="w-full text-[10px]">
                            <thead>
                              <tr style={{ color: C.slateMuted }}>
                                <th className="text-left p-1 uppercase text-[9px]">MCI</th>
                                <th className="text-left p-1 uppercase text-[9px]">Receipt</th>
                                <th className="text-left p-1 uppercase text-[9px]">Receipt Date</th>
                                <th className="text-left p-1 uppercase text-[9px]">Vendor</th>
                                <th className="text-right p-1 uppercase text-[9px]">GRN</th>
                                <th className="text-right p-1 uppercase text-[9px]">Pending</th>
                                <th className="text-right p-1 uppercase text-[9px]">Accepted</th>
                                <th className="text-right p-1 uppercase text-[9px]">Issued</th>
                                <th className="text-right p-1 uppercase text-[9px]">Balance</th>
                                <th className="text-right p-1 uppercase text-[9px]">Rejected</th>
                                <th className="text-center p-1 uppercase text-[9px]">Docs</th>
                                <th className="text-center p-1 uppercase text-[9px]">PO</th>
                              </tr>
                            </thead>
                            <tbody>
                              {g.receipts.map((r, ri) => (
                                <tr key={ri} style={{ borderTop: `1px solid ${C.border}` }}
                                  className="hover:bg-blue-50 cursor-pointer"
                                  onClick={(e) => { e.stopPropagation(); onDrillMci?.(r.mciLineNumber); }}
                                  data-testid={`mt-recv-${r.mciLineNumber}-${r.receiptNumber}`}>
                                  <td className="p-1 font-mono font-bold" style={{ color: C.primaryText }}>{r.mciLineNumber}</td>
                                  <td className="p-1 font-mono" style={{ color: C.accent }}>{r.receiptNumber}</td>
                                  <td className="p-1" style={{ color: C.slateMuted }}>{(r.receiptDate || '').slice(0, 10)}</td>
                                  <td className="p-1" style={{ color: C.slate }}>{r.vendor}</td>
                                  <td className="p-1 text-right font-mono" style={{ color: C.primaryText }}>{fmtNum(r.grnQty || r.acceptedQty)}</td>
                                  <td className="p-1 text-right font-mono" style={{ color: C.warn }}>{fmtNum(r.iqcQty)}</td>
                                  <td className="p-1 text-right font-mono font-bold" style={{ color: C.success }}>{fmtNum(r.acceptedQty)}</td>
                                  <td className="p-1 text-right font-mono" style={{ color: C.accent }}>{fmtNum(r.issuedQty)}</td>
                                  <td className="p-1 text-right font-mono font-bold" style={{ color: C.primaryText }}>{fmtNum(r.balanceQty ?? r.acceptedQty)}</td>
                                  <td className="p-1 text-right font-mono" style={{ color: C.danger }}>{fmtNum(r.rejectedQty)}</td>
                                  <td className="p-1 text-center">
                                    {r.documentRef && <a href={r.documentRef} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="GRN Doc"><FileText className="w-3 h-3 inline" style={{ color: C.primaryText }} /></a>}
                                    {r.inspectionReportRef && <a href={r.inspectionReportRef} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Inspection Report" className="ml-1"><ClipboardCheck className="w-3 h-3 inline" style={{ color: C.accent }} /></a>}
                                  </td>
                                  <td className="p-1 text-center">
                                    <button onClick={(e) => { e.stopPropagation(); onPoPerf?.(r.mciLineNumber); }}
                                      className="text-[9px] px-1.5 py-0.5 rounded font-bold"
                                      style={{ background: 'var(--card-bg-alt)', color: C.primaryText, border: `1px solid ${C.border}` }}
                                      data-testid={`po-perf-btn-${r.mciLineNumber}-${r.receiptNumber}`}
                                      title="View PO delivery / quality / cost performance">
                                      <TrendingUp className="w-2.5 h-2.5 inline mr-0.5" />PO Perf
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Canonical discipline buckets — surfaced in the filter dropdown so
// planners can slice inventory by trade. Backend broadens legacy
// 'Mechanical' matches so all three sub-buckets stay useful even before
// the discipline-backfill runs.
const DISCIPLINE_OPTIONS = [
  { value: '',                        label: 'All Disciplines' },
  { value: 'Structural',              label: 'Structural' },
  { value: 'Piping',                  label: 'Piping' },
  { value: 'Mechanical - Static',     label: 'Mechanical — Static' },
  { value: 'Mechanical - Rotary',     label: 'Mechanical — Rotary' },
  { value: 'Mechanical - Packages',   label: 'Mechanical — Packages' },
  { value: 'Electrical',              label: 'Electrical' },
  { value: 'Instrumentation',         label: 'Instrumentation' },
  { value: 'Civil',                   label: 'Civil' },
];

const SOURCE_OPTIONS = [
  { value: 'ALL',        label: 'All Sources' },
  { value: 'OWNER',      label: "Owner's Material" },
  { value: 'CONTRACTOR', label: 'Contractor Procured' },
];

// Persisted-filter store — keeps discipline / source / scope choices
// alive across page navigation so a planner isn't forced to redial
// filters each time they hop into Material Tracking.
const MT_FILTER_KEY = (pid) => `mt-panel-filters:${pid || 'default'}`;
const _mtReadPersisted = (pid) => {
  try {
    const raw = localStorage.getItem(MT_FILTER_KEY(pid));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
};

export default function MaterialTrackingPanel({ projectId }) {
  const persisted = _mtReadPersisted(projectId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('ALL');
  const [disciplineFilter, setDisciplineFilter] = useState(persisted.discipline || '');
  const [sourceFilter, setSourceFilter] = useState(persisted.source || 'ALL');
  const [showBackfill, setShowBackfill] = useState(false);
  const [drillMci, setDrillMci] = useState(null);
  const [poPerfMci, setPoPerfMci] = useState(null);
  // NEW · Warehouse Inventory vs Site Station Inventory pill sub-tabs
  const [scope, setScope] = useState(persisted.scope || 'warehouse');

  // Persist filter picks to localStorage on every change.
  useEffect(() => {
    try {
      localStorage.setItem(MT_FILTER_KEY(projectId),
        JSON.stringify({ discipline: disciplineFilter,
                          source: sourceFilter, scope }));
    } catch { /* ignore quota */ }
  }, [projectId, disciplineFilter, sourceFilter, scope]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (disciplineFilter) qs.set('discipline', disciplineFilter);
      if (sourceFilter && sourceFilter !== 'ALL') qs.set('materialSource', sourceFilter);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const { data: d } = await api.get(`/v1/projects/${projectId}/material-tracking/inventory${suffix}`);
      setData(d);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [projectId, disciplineFilter, sourceFilter]);

  useEffect(() => {
    load();
    const h = () => load();
    window.addEventListener('mci-register-updated', h);
    return () => window.removeEventListener('mci-register-updated', h);
  }, [load]);

  const k = data?.kpis || {};

  return (
    <div className="p-3 space-y-3" data-testid="material-tracking-panel">
      {/* NEW · Warehouse vs Site Station pill sub-tabs */}
      <div className="flex items-center gap-1 border-b" style={{ borderColor: C.border }}>
        {[
          { k: 'warehouse', label: 'Warehouse Inventory', hint: 'IQC-accepted GRN + station returns', color: 'var(--tone-navy-text)' },
          { k: 'station',   label: 'Site Station Inventory', hint: 'MIR-issued from warehouses',       color: 'var(--tone-teal-text)' },
        ].map(t => (
          <button key={t.k} onClick={() => setScope(t.k)}
            data-testid={`mtm-inv-scope-${t.k}`}
            className="px-3 py-1.5 text-[11px] font-semibold flex items-center gap-1.5"
            title={t.hint}
            style={{
              color: scope === t.k ? 'var(--text-primary)' : C.slateMuted,
              borderBottom: scope === t.k ? `2px solid ${t.color}` : '2px solid transparent',
            }}>
            <Warehouse className="w-3 h-3" style={{ color: t.color }} />
            {t.label}
          </button>
        ))}
      </div>
      {/* KPI band */}
      <div className="grid grid-cols-6 gap-2">
        {[
          { lbl: 'Bins', val: k.bins || 0, color: C.primaryText, icon: Warehouse },
          { lbl: 'Balance Stock', val: fmtNum(k.balance ?? k.totalAccepted), color: C.success, icon: Package },
          { lbl: 'Issued (all-time)', val: fmtNum(k.issued || 0), color: C.accent, icon: Package },
          { lbl: 'Issued Today', val: fmtNum(k.issuedToday || 0), color: '#F26B1F', icon: Package,
            testId: 'kpi-issued-today' },
          { lbl: 'Rejected', val: fmtNum(k.rejected), color: C.danger, icon: Package },
          { lbl: 'Unassigned MCIs', val: k.unlocatedMcis || 0, color: k.unlocatedMcis ? C.danger : C.slateMuted, icon: Warehouse },
        ].map((m, i) => (
          <div key={i} className="p-2 rounded-lg border" style={{ borderColor: C.border, background: 'var(--card-bg)' }}
            data-testid={m.testId}>
            <div className="flex items-center gap-1">
              <m.icon className="w-3 h-3" style={{ color: m.color }} />
              <div className="text-[9px] uppercase" style={{ color: C.slateMuted }}>{m.lbl}</div>
            </div>
            <div className="text-lg font-bold font-mono mt-0.5" style={{ color: m.color }}>{m.val}</div>
          </div>
        ))}
      </div>

      {/* Discipline + Source filters — sit above the universal filter bar */}
      <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg border" style={{ borderColor: C.border, background: 'var(--card-bg-alt)' }}>
        <div className="text-[9px] uppercase font-bold" style={{ color: C.slateMuted }}>Filters:</div>
        <select value={disciplineFilter} onChange={e => setDisciplineFilter(e.target.value)}
          className="text-[11px] px-2 py-1 rounded border font-semibold"
          style={{ borderColor: C.border, background: 'var(--card-bg)', color: disciplineFilter ? C.primaryText : C.slate }}
          data-testid="mt-discipline-filter">
          {DISCIPLINE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
          className="text-[11px] px-2 py-1 rounded border font-semibold"
          style={{ borderColor: C.border, background: 'var(--card-bg)', color: sourceFilter !== 'ALL' ? '#F26B1F' : C.slate }}
          data-testid="mt-source-filter">
          {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {(disciplineFilter || sourceFilter !== 'ALL') && (
          <button onClick={() => { setDisciplineFilter(''); setSourceFilter('ALL'); }}
            className="text-[10px] px-2 py-1 rounded font-semibold"
            style={{ background: 'var(--tone-red-bg)', color: 'var(--tone-red-text)' }}
            data-testid="mt-clear-filters">
            Clear
          </button>
        )}
        <div className="ml-auto text-[10px]" style={{ color: C.slateMuted }}>
          Filtered view · {k.bins || 0} bins · <b style={{ color: C.success }}>{fmtNum(k.balance)}</b> balance
        </div>
      </div>

      <UniversalFilterBar
        search={q} onSearchChange={setQ} searchPlaceholder="Search MCI / material / vendor / receipt…"
        category={catFilter} onCategoryChange={setCatFilter}
        categoryOptions={[{ value:'ALL', label:'ALL' }, { value:'BULK', label:'BULK' }, { value:'TAGGED', label:'TAGGED' }]}
        totalCount={(data?.bins || []).reduce((s, b) => s + b.itemCount, 0)}
        totalLabel="items in stock"
        right={
          <div className="flex items-center gap-2">
            {(data?.bins || []).some(b => b.location === 'UNASSIGNED') && (
              <Button size="sm" onClick={() => setShowBackfill(true)}
                className="h-8 text-xs text-white font-semibold"
                style={{ background: C.accent }}
                data-testid="mt-backfill-btn"
                title="Bulk-assign every UNASSIGNED receipt to a warehouse bin in one shot">
                <Wand2 className="w-3.5 h-3.5 mr-1" /> Backfill Locations
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={load} data-testid="mt-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
        }
      />

      {loading && <div className="p-6 flex items-center justify-center gap-2 text-sm" style={{ color: C.slateMuted }}><Loader2 className="w-4 h-4 animate-spin" /> Loading inventory…</div>}
      {!loading && (data?.bins || []).length === 0 && (
        <div className="p-6 text-center text-xs rounded border" style={{ color: C.slateMuted, borderColor: C.border }}>
          No IQC-Accepted stock yet. Log a receipt via MCI drilldown → <b>Log Receipt</b>, run IQC → <b>Inspect Pending</b>, and mark qty as Accepted to see it here.
        </div>
      )}
      {!loading && (data?.bins || []).map(b => {
        // Partition by warehouse type: BIN row locations (e.g. "OWN-BIN·PIP-01") vs SITE_STATION rows ("SS-01·PIP-01")
        const isStation = String(b.location || '').startsWith('SS-');
        if (scope === 'warehouse' && isStation) return null;
        if (scope === 'station'   && !isStation) return null;
        return (
          <BinCard key={b.location} bin={b} catFilter={catFilter} disciplineFilter={disciplineFilter} q={q}
            onDrillMci={setDrillMci} onPoPerf={setPoPerfMci} projectId={projectId} />
        );
      })}

      {showBackfill && (
        <BackfillDialog projectId={projectId}
          onClose={() => setShowBackfill(false)}
          onApplied={load} />
      )}
      {drillMci && (
        <MciPackageDrawer projectId={projectId} mci={drillMci}
          onClose={() => setDrillMci(null)} />
      )}
      {poPerfMci && (
        <PoPerformanceDrawer projectId={projectId} mciLineNumber={poPerfMci}
          onClose={() => setPoPerfMci(null)} />
      )}
    </div>
  );
}
